import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { QRCodeCanvas } from 'qrcode.react';
import { GET_DOWNLOAD_URLS } from './apollo/queries';
import { PLAY_NOW_URL } from './config';
import { splatSVG, stackSVG, SPLATS, HAZES } from './fx';
import SvgSymbols from './components/SvgSymbols';
import logoImage from './assets/images/logo.jpg';
import './HomePage.css';

const VIEWBOX = { bill: '0 0 160 70', die: '0 0 100 100', chip: '0 0 100 100', crown: '0 0 100 80', sparkle: '0 0 100 100' };
const GLOW_YEL = { className: 'glow-yel', style: { color: 'var(--yellow)' } };
const SPARKLE = { className: 'twinkle', style: { color: '#fff', filter: 'drop-shadow(0 0 6px #fff)' } };
const BLUE_CHIP = { style: { '--c1': '#1e5bff', '--c2': '#3d7bff' } };

// Floating decorations (positions in % of the art). `sym` = sprite symbol, `dollar` = "$" glyph, `stack` = [kind, count]
const DECOS = [
  // mobile
  { mob: true, style: { left: '-6%', top: '30.5%', width: '15%', '--d': 26, '--t': '6s', '--r': '-24deg' }, sym: 'bill' },
  { mob: true, style: { left: '-7%', top: '86%', width: '15%', '--d': 34, '--t': '7.5s', '--r': '12deg' }, sym: 'die', svg: { className: 'glow-pink' } },
  { mob: true, style: { right: '-6%', top: '47%', width: '11%', '--d': 30, '--t': '5.5s', '--r': '-20deg' }, sym: 'chip', svg: BLUE_CHIP },
  { mob: true, style: { left: '30%', top: '57.5%', width: '4%', '--d': 6, '--t': '3s' }, sym: 'sparkle', svg: SPARKLE },
  { mob: true, style: { left: '88%', top: '58%', width: '3.4%', '--d': 6, '--t': '3.6s' }, sym: 'sparkle', svg: { ...SPARKLE, style: { ...SPARKLE.style, '--dl': '-1s' } } },
  // desktop
  { style: { left: '31%', top: '3%', width: '6.5%', '--d': 14, '--t': '7s', '--r': '-16deg' }, sym: 'bill' },
  { style: { left: '44%', top: '-3%', width: '9%', '--d': 30, '--t': '8s', '--r': '14deg' }, sym: 'bill' },
  { style: { left: '50.5%', top: '5%', width: '4%', '--d': 10, '--t': '5s', '--r': '8deg' }, sym: 'crown', svg: GLOW_YEL },
  { style: { left: '21%', top: '66%', width: '3%', '--d': 12, '--t': '6s', '--r': '-10deg' }, sym: 'crown', svg: GLOW_YEL },
  { style: { left: '72.5%', top: '4%', '--d': 16, '--t': '6.5s', '--fs': '3.2vw', '--r': '-10deg' }, dollar: true },
  { style: { left: '64.5%', top: '69%', '--d': 18, '--t': '5.8s', '--fs': '2.6vw', '--r': '12deg' }, dollar: true },
  { style: { left: '20.5%', top: '8%', '--d': 12, '--t': '7s', '--fs': '2vw', '--r': '8deg' }, dollar: true },
  { style: { left: '24.5%', top: '62%', width: '4.6%', '--d': 18, '--t': '6.2s', '--r': '-8deg' }, sym: 'die', svg: { className: 'glow-pink' } },
  { style: { left: '23%', top: '84%', width: '9%', '--d': 42, '--t': '8.5s', '--r': '16deg' }, sym: 'die', svg: { className: 'glow-pink' } },
  { style: { left: '69%', top: '-4%', width: '6%', '--d': 38, '--t': '7.2s', '--r': '-28deg' }, sym: 'die', svg: { className: 'glow-pink' } },
  { style: { left: '55.6%', top: '69%', width: '2.8%', '--d': 20, '--t': '5s', '--r': '-22deg' }, sym: 'chip' },
  { style: { left: '36%', top: '88%', width: '5%', '--d': 36, '--t': '6.8s', '--r': '28deg' }, sym: 'chip', svg: BLUE_CHIP },
  { style: { left: '58%', top: '78%', width: '3.2%', '--d': 10, '--t': '9s', '--a': '5px' }, stack: ['gold', 9] },
  { style: { left: '61.2%', top: '81.5%', width: '3.2%', '--d': 10, '--t': '8s', '--a': '5px' }, stack: ['blue', 7] },
  { style: { left: '94.5%', top: '58%', width: '5.4%', '--d': 22, '--t': '10s', '--a': '6px' }, stack: ['coin', 16] },
  { style: { left: '90%', top: '72%', width: '4.6%', '--d': 16, '--t': '9s', '--a': '5px' }, stack: ['coin', 11] },
];

const SPLAT_HTML = SPLATS.map(s => splatSVG(s[4], s[3]));
// seeds 21, 22, … in document order, as in the original artwork
let stackSeed = 21;
const DECO_HTML = DECOS.map(d => d.stack && stackSVG(d.stack[0], d.stack[1], stackSeed++));

function Use({ id, ...props }) {
  return <svg viewBox={VIEWBOX[id]} {...props}><use href={`#${id}`} /></svg>;
}

function Deco({ deco, stackHtml }) {
  const className = 'deco ' + (deco.mob ? 'mob-only' : 'desk-only');
  if (deco.stack) {
    return <div className={className} style={deco.style} dangerouslySetInnerHTML={{ __html: stackHtml }} />;
  }
  return (
    <div className={className} style={deco.style}>
      {deco.dollar ? <div className="dollar">$</div> : <Use id={deco.sym} {...deco.svg} />}
    </div>
  );
}

// Disabled until the store URL arrives from the backend
function storeLinkProps(url) {
  return url
    ? { href: url, target: '_blank', rel: 'noopener noreferrer' }
    : { 'aria-disabled': true, onClick: e => e.preventDefault() };
}

function HomePage() {
  const stageRef = useRef(null);
  const dialogRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);

  const { data } = useQuery(GET_DOWNLOAD_URLS, { errorPolicy: 'all' });
  const iosUrl = data?.getCashkingsDownloadUrls?.ios;
  const androidUrl = data?.getCashkingsDownloadUrls?.android;

  // pointer parallax for the floating decorations
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || window.matchMedia('(prefers-reduced-motion: reduce)').matches || !window.matchMedia('(pointer:fine)').matches) return;
    let raf = 0, tx = 0, ty = 0;
    const onMove = e => {
      tx = (e.clientX / window.innerWidth - .5) * 2;
      ty = (e.clientY / window.innerHeight - .5) * 2;
      if (!raf) raf = requestAnimationFrame(() => {
        raf = 0;
        stage.style.setProperty('--mx', tx.toFixed(3));
        stage.style.setProperty('--my', ty.toFixed(3));
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  const openInstall = () => {
    const dlg = dialogRef.current;
    if (typeof dlg.showModal === 'function') dlg.showModal(); else dlg.setAttribute('open', '');
  };
  const closeInstall = () => dialogRef.current.close();

  return (
    <>
      <SvgSymbols />

      <h1 className="sr-only">Cash Kings – download the mobile casino app</h1>

      <main className="wrap">
        <div className="stage" id="stage" ref={stageRef}>

          {/* splatters & glow layered on the background */}
          <div id="fx">
            {SPLATS.map((s, i) => (
              <div key={i} className="splat desk-only" style={{ left: s[0] + '%', top: s[1] + '%', width: s[2] + '%', opacity: s[5] }}
                dangerouslySetInnerHTML={{ __html: SPLAT_HTML[i] }} />
            ))}
            {HAZES.map((h, i) => (
              <div key={i} className={'haze ' + (h[4] ? 'desk-only' : 'mob-only')}
                style={{ left: h[0], top: h[1], width: h[2], aspectRatio: 1.6, background: h[3] }} />
            ))}
          </div>

          <div className="shine m-shine" aria-hidden="true"></div>

          {/* GOOD VIBES / BIG WINS (desktop) */}
          <div className="vibes desk-only" aria-hidden="true">
            <div className="p">GOOD</div><div className="p">VIBES</div><div className="p">BIG</div><div className="c">WINS</div>
            <svg className="crown glow-yel" style={{ color: 'var(--yellow)' }}><use href="#crown" /></svg>
          </div>

          {/* Phone with login screen (desktop) */}
          <div className="phone desk-only">
            <div className="phone-frame">
              <div className="screen">
                <div className="status">
                  <span>9:41</span><span className="island"></span>
                  <span className="sicons" aria-hidden="true">
                    <svg viewBox="0 0 18 12"><rect x="0" y="8" width="3" height="4" rx="1" fill="#fff" /><rect x="5" y="5.5" width="3" height="6.5" rx="1" fill="#fff" /><rect x="10" y="3" width="3" height="9" rx="1" fill="#fff" /><rect x="15" y="0" width="3" height="12" rx="1" fill="#fff" /></svg>
                    <svg viewBox="0 0 16 12"><path d="M8 11.5 5.6 9a3.4 3.4 0 0 1 4.8 0zM3.4 6.8a6.5 6.5 0 0 1 9.2 0l-1.4 1.4a4.5 4.5 0 0 0-6.4 0zM1 4.4a9.9 9.9 0 0 1 14 0l-1.4 1.4a7.9 7.9 0 0 0-11.2 0z" fill="#fff" /></svg>
                    <svg viewBox="0 0 27 12"><rect x=".5" y=".5" width="23" height="11" rx="3" fill="none" stroke="#fff" opacity=".5" /><rect x="2" y="2" width="20" height="8" rx="2" fill="#fff" /><rect x="24.5" y="4" width="2" height="4" rx="1" fill="#fff" opacity=".5" /></svg>
                  </span>
                </div>
                <img className="p-logo" src={logoImage} alt="Cash Kings" />
                <form className="login" action="#" onSubmit={e => e.preventDefault()}>
                  <label className="field">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="7.5" r="4.5" /><path d="M3 21c0-5 4-8 9-8s9 3 9 8z" /></svg>
                    <input type="text" name="username" placeholder="Username" autoComplete="username" aria-label="Username" />
                  </label>
                  <label className="field">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 10V7a5 5 0 0 1 10 0v3h1.5A1.5 1.5 0 0 1 20 11.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 20.5v-9A1.5 1.5 0 0 1 5.5 10zm2.5 0h5V7a2.5 2.5 0 0 0-5 0z" /></svg>
                    <input type={showPassword ? 'text' : 'password'} name="password" placeholder="Password" autoComplete="current-password" aria-label="Password" />
                    <button type="button" className="eye" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword(v => !v)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" />
                        {!showPassword && <path d="M4 20 20 4" />}
                      </svg>
                    </button>
                  </label>
                  <label className="remember">
                    <input type="checkbox" defaultChecked />
                    <span className="box"><svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6" /></svg></span>
                    Remember Me
                  </label>
                  <button className="login-btn" type="submit">LOG IN</button>
                  <p className="reg">Not a Member? <a href={PLAY_NOW_URL} target="_blank" rel="noopener noreferrer">REGISTER</a></p>
                </form>
                <div className="slot-row" aria-hidden="true">
                  <svg className="chip" style={{ left: '6%', '--c1': '#1e5bff', '--c2': '#3d7bff', transform: 'rotate(-14deg)' }}><use href="#chip" /></svg>
                  <div className="slot">
                    <svg className="crown glow-yel" style={{ color: 'var(--yellow)' }}><use href="#crown" /></svg>
                    <span>7</span><span>7</span><span>7</span>
                  </div>
                  <svg className="chip" style={{ right: '6%', transform: 'rotate(12deg)' }}><use href="#chip" /></svg>
                </div>
              </div>
            </div>
          </div>

          {/* Left / bottom content */}
          <section className="panel" aria-label="Download Cash Kings">
            <div className="blk b-dl">
              <div className="dl-box pop">
                <span className="ink dl" data-text="DOWNLOAD"><span>DOWNLOAD</span></span>
                <svg className="dl-crown glow-yel" style={{ color: 'var(--yellow)' }} aria-hidden="true"><use href="#crown" /></svg>
                <svg className="spark twinkle" style={{ '--s': 34, left: '2%', top: '4%' }} aria-hidden="true"><use href="#sparkle" /></svg>
                <svg className="spark twinkle" style={{ '--s': 26, left: '40%', top: '-6%', '--dl': '-.8s' }} aria-hidden="true"><use href="#sparkle" /></svg>
                <svg className="spark twinkle" style={{ '--s': 38, right: '-2%', top: '2%', '--dl': '-1.4s' }} aria-hidden="true"><use href="#sparkle" /></svg>
                <svg className="spark twinkle" style={{ '--s': 22, right: '20%', bottom: '6%', '--dl': '-.4s' }} aria-hidden="true"><use href="#sparkle" /></svg>
              </div>
            </div>
            <div className="blk b-sub rise" style={{ '--dl': '.7s' }}>
              <span className="ink sub" data-text="Cash Kings Mobile App"><span>Cash Kings Mobile App</span></span>
            </div>
            <div className="blk b-tag rise" style={{ '--dl': '.85s' }}>
              <p className="tag" style={{ margin: 0 }}>
                <b className="c1">Spin.</b><b className="c2">Play.</b><b className="c3">Win.</b><b className="c4">Repeat.</b>
                <svg className="desk-only glow-yel" style={{ color: 'var(--yellow)' }} aria-hidden="true" viewBox="0 0 100 80"><use href="#crown" /></svg>
              </p>
            </div>
            <div className="blk b-bring rise" style={{ '--dl': '.95s' }}>
              <p className="bring" style={{ margin: 0 }}>Bring the casino vibe anywhere.</p>
            </div>

            <div className="blk b-qr rise" style={{ '--dl': '1.05s' }}>
              <div className="qr-zone">
                <svg className="arrow a-pink desk-only" viewBox="0 0 190 130" aria-hidden="true"><g filter="url(#spray)"><path d="M10 120 Q70 95 150 35" strokeWidth="14" /><path d="M112 30 L156 30 L150 72" strokeWidth="14" /><path d="M60 102 v26 M98 80 v18" strokeWidth="5" /></g></svg>
                <svg className="arrow a-yel desk-only" viewBox="0 0 120 70" aria-hidden="true"><g filter="url(#spray)"><path d="M8 50 Q50 30 100 30" strokeWidth="11" /><path d="M80 12 L104 30 L82 48" strokeWidth="11" /><path d="M40 42 v22" strokeWidth="5" /></g></svg>
                <svg className="arrow a-cyan desk-only" viewBox="0 0 190 130" aria-hidden="true"><g filter="url(#spray)"><path d="M180 120 Q120 95 40 35" strokeWidth="14" /><path d="M78 30 L34 30 L40 72" strokeWidth="14" /><path d="M130 100 v26" strokeWidth="5" /></g></svg>
                <div className="qr" role="img" aria-label="Scan to download Cash Kings">
                  <i></i><i></i><i></i><i></i>
                  <div className="qr-box">
                    <div className="qr-code"><QRCodeCanvas value={PLAY_NOW_URL} size={256} level="H" marginSize={0} /></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="blk b-badges rise" style={{ '--dl': '1.15s' }}>
              <div className="badges">
                <a className="badge" aria-label="Get it on Google Play" {...storeLinkProps(androidUrl)}>
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3.6 1.8 13.4 12 3.6 22.2c-.4-.2-.6-.7-.6-1.2V3c0-.5.2-1 .6-1.2z" fill="#00d7fe" />
                    <path d="M3.6 1.8c.3-.2.8-.2 1.3.1l11.6 6.6-3.1 3.5z" fill="#00f076" />
                    <path d="M3.6 22.2 13.4 12l3.1 3.5-11.6 6.6c-.5.3-1 .3-1.3.1z" fill="#ff3a44" />
                    <path d="m16.5 8.5 3.9 2.2c1 .6 1 2 0 2.6l-3.9 2.2-3.1-3.5z" fill="#ffd000" />
                  </svg>
                  <span><small>GET IT ON</small><strong>Google Play</strong></span>
                </a>
                <a className="badge" aria-label="Download on the App Store" {...storeLinkProps(iosUrl)}>
                  <svg viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M16.4 12.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.6 1.3-.1 1.8-.8 3.3-.8s2 .8 3.4.8c1.4 0 2.3-1.3 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9-.1 0-2.7-1-2.7-4.1zM13.9 5c.7-.9 1.2-2 1.1-3.2-1 0-2.3.7-3 1.6-.7.8-1.2 2-1.1 3.1 1.1.1 2.3-.6 3-1.5z" /></svg>
                  <span><small>Download on the</small><strong>App Store</strong></span>
                </a>
              </div>
            </div>

            <div className="blk b-ctas rise" style={{ '--dl': '1.25s' }}>
              <div className="ctas">
                <a className="cta cta-play" href={PLAY_NOW_URL} target="_blank" rel="noopener noreferrer">
                  <svg className="ic" viewBox="0 0 100 80" style={{ color: 'var(--yellow)', filter: 'drop-shadow(0 0 4px var(--yellow))' }} aria-hidden="true"><use href="#crown" /></svg>
                  <span className="lbl">Play Now</span>
                  <svg className="chev" viewBox="0 0 22 30" fill="none" stroke="#fff" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 5l10 10-10 10" /></svg>
                </a>
                <button className="cta cta-install" type="button" aria-haspopup="dialog" onClick={openInstall}>
                  <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12M6.5 10 12 15.5 17.5 10M4 20h16" /></svg>
                  <span className="lbl">How To Install</span>
                  <svg className="chev" viewBox="0 0 22 30" fill="none" stroke="#fff" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 5l10 10-10 10" /></svg>
                </button>
              </div>
            </div>
          </section>

          {/* Floating decorations */}
          <div id="decos" aria-hidden="true">
            {DECOS.map((deco, i) => (
              <Deco key={i} deco={deco} stackHtml={DECO_HTML[i]} />
            ))}
          </div>
        </div>
      </main>

      {/* How To Install modal */}
      <dialog ref={dialogRef} aria-labelledby="installTitle" onClick={e => { if (e.target === dialogRef.current) closeInstall(); }}>
        <div className="m-in">
          <button className="m-close" type="button" aria-label="Close" onClick={closeInstall}>
            <svg width="16" height="16" viewBox="0 0 16 16" stroke="#fff" strokeWidth="2.4" strokeLinecap="round"><path d="M3 3l10 10M13 3 3 13" /></svg>
          </button>
          <h2 id="installTitle">How To Install</h2>
          <p>Get Cash Kings on your phone in under a minute.</p>
          <div className="m-grid">
            <div className="m-card" style={{ '--c': 'var(--green)' }}>
              <h3><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 9h10v8a1 1 0 0 1-1 1h-1v3h-2v-3h-2v3H9v-3H8a1 1 0 0 1-1-1zM4.5 9A1.5 1.5 0 0 1 6 10.5v5a1.5 1.5 0 0 1-3 0v-5A1.5 1.5 0 0 1 4.5 9zm15 0a1.5 1.5 0 0 1 1.5 1.5v5a1.5 1.5 0 0 1-3 0v-5A1.5 1.5 0 0 1 19.5 9zM7 8a5 5 0 0 1 2.4-4.1l-1-1.6.8-.5 1.1 1.7a5 5 0 0 1 3.4 0l1.1-1.7.8.5-1 1.6A5 5 0 0 1 17 8z" /></svg>Android</h3>
              <ol>
                <li>Scan the QR code or tap <b>Get it on Google Play</b>.</li>
                <li>Tap <b>Install</b> and wait for the download.</li>
                <li>Open Cash Kings, register and log in.</li>
              </ol>
            </div>
            <div className="m-card" style={{ '--c': 'var(--cyan)' }}>
              <h3><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.4 12.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.6 1.3-.1 1.8-.8 3.3-.8s2 .8 3.4.8c1.4 0 2.3-1.3 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9-.1 0-2.7-1-2.7-4.1zM13.9 5c.7-.9 1.2-2 1.1-3.2-1 0-2.3.7-3 1.6-.7.8-1.2 2-1.1 3.1 1.1.1 2.3-.6 3-1.5z" /></svg>iPhone (iOS)</h3>
              <ol>
                <li>Point your Camera at the QR code or tap <b>App Store</b>.</li>
                <li>Tap <b>Get</b> and confirm with Face ID.</li>
                <li>Open Cash Kings from your home screen.</li>
              </ol>
            </div>
          </div>
          <p className="m-note">18+ only. Play responsibly.</p>
        </div>
      </dialog>
    </>
  );
}

export default HomePage;
